import { useReactiveVar } from '@apollo/client';
import { videoSessionStore } from 'apolloGraphql/mml-apollo-cache';
import { logger } from 'logger';
import { createContext, useContext } from 'react';
import { VideoProperties, VideoPropertyType } from 'state-machines/analytic-states';
import { DEFAULT_LIVE_PLAYER_STATE, LivePlayerState } from 'state-machines/live-player-states';
import { VIDEO_ADS_INITIAL_STATE, VideoAdsState } from 'state-machines/video-ads-state';
import { DeepPartial } from 'utility-types';
import deeperMerge from 'utils/deepMerge';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOG = logger.getLogger('new-video-session-state');

// TODO:  use this instead of the hard coded/fixed names
export type NewVideoSessionName = `session_${number}`;

/**
 * The different types of video players.  Add to this enum as necessary.
 */
export enum OldSessionName {
  FirstPlayer = 'firstPlayer',
  FirstPlayerOriginal = 'firstPlayer_original',
  SecondPlayer = 'secondPlayer',
  AdPlayer = 'adPlayer',
}

export type VideoSessionName = OldSessionName | NewVideoSessionName;

export type VideoSessions = Map<VideoSessionName, VideoSession>;

export interface VideoSession {
  livePlayerState: LivePlayerState;
  videoAds: VideoAdsState;
  videoProperties: VideoPropertyType;
  sessionName: VideoSessionName;
}

export const INITIAL_VIDEO_SESSION = Object.freeze<VideoSession>({
  livePlayerState: DEFAULT_LIVE_PLAYER_STATE,
  videoProperties: VideoProperties,
  videoAds: VIDEO_ADS_INITIAL_STATE,
  sessionName: OldSessionName.FirstPlayer,
});

// FIXME:  adding a random empty session because stuff quickly crashes otherwise?
export const INITIAL_VIDEO_SESSION_STATE: Map<VideoSessionName, VideoSession> = Object.freeze<
  Map<VideoSessionName, VideoSession>
>(new Map([[OldSessionName.FirstPlayer, INITIAL_VIDEO_SESSION]]));

let nextAvailableVideoSession = 0;

export function getNextAvailableVideoSessionIdentifier(): NewVideoSessionName {
  nextAvailableVideoSession++;
  const returnValue = `session_${nextAvailableVideoSession}`;
  console.log(`getNextAvailableVideoSessionIdentifier: ${returnValue}`);
  return returnValue as NewVideoSessionName;
}

export function getPrimaryVideoSession(): VideoSession {
  const videoSessions = videoSessionStore();
  let primarySession: VideoSession | undefined = undefined;
  for (const session of videoSessions.values()) {
    if (session.livePlayerState?.primarySession) primarySession = session;
  }
  if (!primarySession) {
    throw new Error('Something went wrong.  There is no primary session in the videoSessionStore');
  } else {
    return primarySession;
  }
}

export function isCurrentlyPlaying(bracketId: number | undefined | null): boolean {
  for (const session of videoSessionStore().values()) {
    if (session.videoProperties.videoTitleId === bracketId + '') return true;
  }
  return false;
}

export function updateVideoSession(
  params: VideoSession,
  shouldMerge: false,
  sessionName?: VideoSessionName,
): VideoSessions;
export function updateVideoSession(
  params: DeepPartial<VideoSession>,
  shouldMerge?: true,
  sessionName?: VideoSessionName,
): VideoSessions;
export function updateVideoSession(
  params: DeepPartial<VideoSession> | VideoSession,
  shouldMerge = true,
  sessionName?: VideoSessionName,
) {
  // TODO:  this is a bit expensive due to the merging and spread operators
  const videoSessions = videoSessionStore();
  const targetSessionName = sessionName ?? getPrimaryVideoSession()?.sessionName;
  if (!targetSessionName) {
    throw new Error('Something went wrong.  There is no primary session in the videoSessionStore');
  }

  const targetSession = videoSessions.get(targetSessionName);

  const updatedVideoSession = (
    shouldMerge && targetSession ? deeperMerge(targetSession, params) : params
  ) as VideoSession;
  videoSessions.set(targetSessionName, updatedVideoSession);

  LOG.debug('updateVideoSession', updatedVideoSession);
  return videoSessionStore(new Map(videoSessions.entries()));
}

export function updatePrimarySession(sessionName: VideoSessionName) {
  const updatedVideoSessions = new Map<VideoSessionName, VideoSession>();
  // NOTE: converting to array because I can't get the polyfill for Iterator#find to work
  Array.from(videoSessionStore().entries()).forEach((entry) => {
    if (entry[0] === sessionName) {
      updatedVideoSessions.set(entry[0], Object.assign({}, entry[1], { livePlayerState: { primarySession: true } }));
    } else {
      updatedVideoSessions.set(entry[0], Object.assign({}, entry[1], { livePlayerState: { primarySession: false } }));
    }
  });
  LOG.debug('updatePrimarySession', updatedVideoSessions);
  return videoSessionStore(new Map(updatedVideoSessions.entries()));
}

// TODO: this can probably be removed?  or at least the name doesn't make sense anymore
export function resetPrimarySessionToLeftSide() {
  const videoSession = videoSessionStore();

  updatePrimarySession(OldSessionName.FirstPlayer);
  const primarySession = videoSession.get(OldSessionName.FirstPlayer);

  if (!primarySession) throw new Error('Something went wrong.  There is no primary session in the videoSessionStore');

  return videoSessionStore(new Map(videoSession.entries()));
}

export function initNewVideoSession(sessionName: VideoSessionName) {
  const videoSessions = videoSessionStore();

  // if session already exists, just return it
  const videoSession = videoSessions.get(sessionName);
  if (videoSession) return videoSession;

  videoSessions.set(sessionName, Object.assign({}, INITIAL_VIDEO_SESSION, { sessionName: sessionName }));
  LOG.debug('updatePrimarySession', videoSessions);
  return videoSessionStore(new Map(videoSessions.entries())).get(sessionName) as VideoSession;
}

export function removeVideoSession(sessionName: VideoSessionName) {
  LOG.debug('removeVideoSession', sessionName);
  const videoSessions = videoSessionStore();
  videoSessions.delete(sessionName);
  videoSessionStore(new Map(videoSessions.entries()));

  if (videoSessions.size > 0) {
    const nextAvailableSession = videoSessions.entries().next().value?.[0];
    if (nextAvailableSession) updatePrimarySession(nextAvailableSession);
  } else {
    // No sessions left, re-initialize
    videoSessionStore(INITIAL_VIDEO_SESSION_STATE);
  }
}

export function usePrimarySession() {
  const videoSessions = useReactiveVar(videoSessionStore);
  for (const session of videoSessions.values()) {
    if (session.livePlayerState?.primarySession) return session;
  }
  return undefined;
}

/**
 * Returns true if any player is playing ads, false otherwise
 */
export function useAdsPlaying() {
  const videoSessions = useReactiveVar(videoSessionStore);
  for (const session of videoSessions.values()) {
    if (session.videoAds.midRoll || session.videoAds.preRoll) return true;
  }
  return false;
}

/**
 * Returns true if passed session is playing any ads, false otherwise
 */
export function useAdsPlayingFor(sessionName: VideoSessionName) {
  const videoSessions = useReactiveVar(videoSessionStore);
  const session = videoSessions.get(sessionName);
  return !!(session?.videoAds.midRoll || session?.videoAds.preRoll);
}

/**
 * Returns true if passed session is playing midroll ads, false otherwise
 */
export function useMidRollPlayingFor(sessionName: VideoSessionName) {
  const videoSessions = useReactiveVar(videoSessionStore);
  return !!videoSessions.get(sessionName)?.videoAds.midRoll;
}

/**
 * Returns true if passed session is playing preroll ads, false otherwise
 */
export function usePreRollPlayingFor(sessionName: VideoSessionName) {
  const videoSessions = useReactiveVar(videoSessionStore);
  return !!videoSessions.get(sessionName)?.videoAds.preRoll;
}

export const NewVideoContext = createContext<VideoContextType>({
  initNewVideoSession,
  resetPrimarySessionToLeftSide,
  updatePrimarySession,
  updateVideoSession,
  usePrimarySession,
  videoSession: INITIAL_VIDEO_SESSION,
  videoSessionName: OldSessionName.FirstPlayer,
});
NewVideoContext.displayName = 'NewVideoContext';

export const useVideoContext = (): VideoContextType => useContext(NewVideoContext);

export type VideoContextType = {
  initNewVideoSession: typeof initNewVideoSession;
  resetPrimarySessionToLeftSide: typeof resetPrimarySessionToLeftSide;
  updatePrimarySession: typeof updatePrimarySession;
  updateVideoSession: typeof updateVideoSession;
  usePrimarySession: typeof usePrimarySession;
  videoSession: VideoSession;
  videoSessionName: VideoSessionName;
};
